/**
 * Authoring-time validation for formula gates (field unlock, signature,
 * whole-form completion — every surface GateFormulaEditor serves).
 *
 * Why this is a module and not inline JSX: `vitest.config.ts` is
 * `environment: 'node'` with `include: ['src/**\/*.test.ts']`, so a `.tsx`
 * test would silently never run. The policy and every string an author reads
 * therefore live here, and the component only renders what this returns.
 *
 * This is design-time feedback ONLY. `evaluateGateFormula` remains the single
 * run-time authority and its fail-closed contract is untouched — a gate this
 * module calls `pass` can still evaluate false at fill time, which is the
 * whole point of a gate.
 */
import { analyzeFormula, extractFormulaRefs, formulaToHuman } from '@/lib/formula'
import { isCrossFormRef } from '@/lib/crossForm'

export type GateValidation =
  /** No formula typed yet — show help, not a verdict. */
  | { state: 'empty' }
  /**
   * Well-formed and every reference resolves. `truthiness` is true when the
   * formula returns a value rather than a definite true/false, i.e. it gates
   * on "this field has been filled in" — a legitimate gate, so a pass.
   */
  | { state: 'pass'; summary: string; truthiness: boolean }
  /**
   * Unusable as written; `message` names the specific problem. `kind`
   * separates the two causes because the remedy differs: an unknown
   * reference is fixed with the field picker, a syntax error is not.
   */
  | { state: 'fail'; kind: 'unknown-ref' | 'syntax'; message: string }

/**
 * A ref is reportable as unknown only when it is same-form-shaped and absent
 * from the label map. Cross-form refs (`{handle.fieldId}`) are exempt: the
 * picker only offers them once a procedure-template context is selected, so
 * outside that context a perfectly valid stored ref is simply not in
 * `labelById` and flagging it would be a false positive.
 */
function unknownRefs(formula: string, labelById: Record<string, string>): string[] {
  return extractFormulaRefs(formula).filter(id => !(id in labelById) && !isCrossFormRef(id))
}

/** Field labels for the refs a formula reads, in first-seen order. */
function resolvedRefLabels(formula: string, labelById: Record<string, string>): string[] {
  return extractFormulaRefs(formula).map(id => labelById[id] ?? `{${id}}`)
}

function joinLabels(labels: string[]): string {
  if (labels.length <= 1) return labels[0] ?? ''
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`
}

/**
 * Validate a gate formula against the form definition as it stands right now.
 *
 * Callers pass the current `labelById`, so a gate whose referenced field was
 * later renamed or deleted re-validates to a fail state the next time the
 * panel renders — the stored formula is never trusted as "it was fine when
 * it was authored".
 */
export function validateGateFormula(
  formula: string | undefined,
  labelById: Record<string, string>,
): GateValidation {
  if (!formula || !formula.trim()) return { state: 'empty' }

  // Unknown references first: a typo'd ref usually also fails to parse in a
  // confusing way, and "Unknown field" is the more actionable of the two.
  const unknown = unknownRefs(formula, labelById)
  if (unknown.length > 0) {
    const tokens = unknown.map(id => `{${id}}`).join(', ')
    return {
      state: 'fail',
      kind: 'unknown-ref',
      message: `Unknown field${unknown.length > 1 ? 's' : ''}: ${tokens}.`,
    }
  }

  const analysis = analyzeFormula(formula)
  if (!analysis.parsed) {
    return {
      state: 'fail',
      kind: 'syntax',
      message: analysis.syntaxError ?? "This formula can't be read.",
    }
  }

  // `formulaToHuman` renders any ref it cannot label as "⚠ deleted field".
  // A cross-form ref is legitimately unlabelled outside a procedure-template
  // context (see unknownRefs), so rendering the summary anyway would put a
  // deleted-field warning inside a green "Valid" box — the same
  // contradictory-messaging defect this whole change exists to remove.
  // Drop the summary instead; `gatePassDetail` still names the ref honestly,
  // by its raw token.
  const everyRefLabelled = extractFormulaRefs(formula).every(id => id in labelById)
  const summary = everyRefLabelled ? formulaToHuman(formula, labelById) : ''
  return { state: 'pass', summary, truthiness: analysis.shape === 'value' }
}

/** The sentence shown under a pass state, explaining what the gate will do. */
export function gatePassDetail(
  validation: Extract<GateValidation, { state: 'pass' }>,
  formula: string,
  labelById: Record<string, string>,
): string {
  const names = joinLabels(resolvedRefLabels(formula, labelById))
  if (!validation.truthiness) {
    return names
      ? `Evaluates to true/false using: ${names}.`
      : 'Evaluates to true/false.'
  }
  // Truthiness gate: isTruthy() treats null (blank or missing) as false and
  // every other value — including 0 — as true, so this reads as "once filled".
  return names
    ? `Passes once ${names} has a value. A non-empty value counts as true.`
    : 'Passes on any non-empty value.'
}
